<template>
  <!-- 深色半透明遮罩 -->
  <transition name="fade">
    <div v-if="visible" class="pwa-guide-overlay" @click="handleOverlayClick">
      <!-- 底部弹出的内容区 -->
      <transition name="slide-up">
        <div
          v-if="visible"
          class="pwa-guide-content"
          :class="{
            'is-mobile-landscape': isMobileLandscape,
          }"
          @click.stop
        >
          <div class="title">{{ $t('pwa_guide_title') }}</div>

          <!-- 教程视频 -->
          <div class="video-wrapper">
            <video
              ref="videoRef"
              class="guide-video"
              :src="videoSrc"
              playsinline
              webkit-playsinline
              x5-playsinline
              x5-video-player-type="h5"
              x5-video-player-fullscreen="false"
              x-webkit-airplay="allow"
              muted
              autoplay
              loop
            >
              <p>{{ $t('pwa_video_not_support') }}</p>
            </video>
          </div>

          <!-- 底部操作区 -->
          <div class="dialog-footer">
            <!-- 不再显示 -->
            <el-checkbox v-model="dontShowAgain" size="large" class="dont-show-checkbox">
              {{ $t('pwa_dont_show_again') }}
            </el-checkbox>

            <!-- 知道了按钮 -->
            <el-button class="got-it-btn" round size="large" type="primary" @click="handleClose">
              {{ $t('pwa_got_it') }}
            </el-button>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script setup lang="ts">
/**
 * PWA 安装教程弹窗组件 (仅 iOS 设备)
 *
 * 功能:
 * - 展示如何将网站添加到主屏幕的教程视频
 * - 支持"不再显示"选项
 * - 仅在 iOS 设备(iPhone/iPad)上显示
 */

import { useDeviceDetect } from '@/composables/useDeviceDetect'

interface Props {
  modelValue: boolean
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// 设备检测
const { isMobileLandscape } = useDeviceDetect()

// 视频元素引用
const videoRef = ref<HTMLVideoElement | null>(null)

// 不再显示
const dontShowAgain = ref(false)

// 视频路径
const videoSrc = '/videos/pwa-guide.mp4'

// 控制弹窗显示/隐藏
const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

/**
 * 点击遮罩层关闭弹窗
 */
const handleOverlayClick = () => {
  visible.value = false
}

/**
 * 关闭弹窗
 */
const handleClose = () => {
  // 如果勾选了"不再显示",存储到 sessionStorage
  if (dontShowAgain.value) {
    sessionStorage.setItem('pwa-guide-shown', 'true')
  }

  visible.value = false
}

/**
 * 监听弹窗显示状态,控制视频播放
 */
watch(visible, newVal => {
  if (newVal && videoRef.value) {
    // 弹窗打开时,尝试播放视频
    videoRef.value.play().catch(() => {
      // 自动播放失败时,静默处理
      console.warn('[PWAGuide] 视频自动播放失败')
    })
  } else if (!newVal && videoRef.value) {
    // 弹窗关闭时,暂停视频
    videoRef.value.pause()
  }
})
</script>

<style scoped lang="scss">
// 深色半透明遮罩层
.pwa-guide-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75); // 深色半透明背景
  z-index: 9999;
  display: flex;
  align-items: flex-end; // 内容对齐到底部
}

// 底部弹出的内容区
.pwa-guide-content {
  width: 100%;
  max-height: 85vh; // 最大高度不超过 85% 视口高度
  background: linear-gradient(180deg, #1a1a1a 0%, #2a2a2a 100%); // 深色渐变背景
  border-radius: 20px 20px 0 0; // 顶部圆角
  box-shadow: 0px -4px 24px 0px rgba(0, 0, 0, 0.6);
  padding: 30px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  // 手机横屏: 减少高度和 padding
  &.is-mobile-landscape {
    max-height: 75vh;
    padding: 20px;
  }

  // 手机竖屏: 减少 padding
  @media (max-width: 767px) and (min-height: 501px) {
    padding: 20px;
  }

  // 滚动条样式
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
}

.title {
  font-size: 36px;
  text-align: center;
  margin-bottom: 20px;
  color: #ffffff;
  font-weight: 600;

  // 手机竖屏: 缩小标题
  @media (max-width: 767px) and (min-height: 501px) {
    font-size: 28px;
    margin-bottom: 15px;
  }
}

.video-wrapper {
  max-height: 55vh;
  overflow: hidden;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.5);
  margin-bottom: 20px;

  // 手机横屏: 降低视频最大高度
  &.is-mobile-landscape {
    max-height: 35vh;
  }

  // 手机竖屏: 降低视频最大高度
  @media (max-width: 767px) and (min-height: 501px) {
    max-height: 45vh;
  }
}

.guide-video {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 12px;
}

.dialog-footer {
  display: flex;
  flex-direction: row; // 横向排列
  align-items: center;
  justify-content: space-between; // 左右两端对齐
  gap: 15px;
  margin-top: auto; // 推到底部

  // 手机竖屏: 缩小间距
  @media (max-width: 767px) and (min-height: 501px) {
    gap: 10px;
  }

  .dont-show-checkbox {
    flex-shrink: 0; // 防止复选框被压缩
    margin-right: auto; // 推到最左边

    :deep(.el-checkbox__label) {
      font-size: 24px;
      color: rgba(255, 255, 255, 0.9);
      white-space: nowrap; // 防止文字换行

      // 手机竖屏: 缩小字体
      @media (max-width: 767px) and (min-height: 501px) {
        font-size: 18px;
      }
    }

    :deep(.el-checkbox__inner) {
      width: 28px;
      height: 28px;
      border: 2px solid rgba(255, 255, 255, 0.4);
      background-color: transparent;

      // 手机竖屏: 缩小复选框
      @media (max-width: 767px) and (min-height: 501px) {
        width: 24px;
        height: 24px;
      }

      &::after {
        width: 8px;
        height: 14px;
        border-width: 2px;

        // 手机竖屏: 缩小勾选图标
        @media (max-width: 767px) and (min-height: 501px) {
          width: 6px;
          height: 12px;
        }
      }
    }

    &.is-checked {
      :deep(.el-checkbox__inner) {
        background-color: #ffb12c;
        border-color: #ffb12c;
      }

      :deep(.el-checkbox__label) {
        color: #ffffff;
      }
    }
  }

  .got-it-btn {
    width: 200px; // 固定宽度
    height: 72px;
    font-size: 28px;
    font-weight: 600;
    border: none;
    box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.3);
    transition: all 0.3s;
    background: linear-gradient(180deg, #ffb12c 0%, #ffc445 100%);
    color: #463011;
    border-radius: 12px;

    // 手机竖屏: 缩小按钮尺寸
    @media (max-width: 767px) and (min-height: 501px) {
      width: 160px;
      height: 60px;
      font-size: 24px;
    }

    &:hover {
      box-shadow: 0px 6px 16px 0px rgba(255, 177, 44, 0.5);
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0) scale(0.98);
    }
  }
}

// 遮罩层淡入淡出动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// 内容区从底部滑入/滑出动画
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}
</style>
